/**
 * Shape-preserving split of a segment's ease at progress p in (0, 1) (D§6.4, E§4.7; progress in SECONDS, computed
 * by the caller exactly as Phira computes event progress).
 *
 *  - hold / linear: trivial and exact (both halves keep the ease).
 *  - tween with effective slice [L, R] (the whole curve [0, 1] when Phira ignores the slice): halves get
 *    [L, m] and [m, R] with m = L + (R - L) p. Phira's clamped tween renormalises each half, so with the caller's
 *    split value v(T) = the original curve's value the composite equals the original:
 *      exact  (to f32 rounding) for Sine, Quad, Cubic, Quart, Quint, Circ, Back, Bounce, and for Expo / Elastic
 *             whose original slice already applied (renormalised curves pass through 0 and 1);
 *      approx for UNCLIPPED Expo / Elastic: the whole curve misses 0 / 1 by about 2^-10 (InExpo(0) = 2^-10,
 *             InElastic(0) ~ -0.000488) and the renormalised halves drop that offset (about 0.1 % of the range).
 *  - bezier: de Casteljau subdivision at the parameter where x(t) = p (solved in f64), both halves renormalised
 *    to the unit square; never 'exact' because Phira evaluates each half with its own solver, whose error depends
 *    on the curve (Newton from a poor table guess can land far off where x'(t) is near 0, e.g. (0, 0, 0, 1) near
 *    its start). Phira shares bezier curves by a 0.01-quantised cache key, first event wins: when the right half's
 *    key equals the left half's key but the points differ, Phira draws the right half with the LEFT half's points,
 *    and the right half is measured that way.
 *
 * Every half is MEASURED, and the measurement decides its class (APPROX_LIMIT = 1e-3, i.e. 0.1 % of the value
 * range, the contract's meaning of 'approx'):
 *  - 'bake'   at least one half is listed in `bakeHalves`: a half Phira cannot represent or the model refuses
 *             (tween: an empty slice or f(m) == f(L) / f(R) == f(m) in f32, the division by zero of Phira's
 *             clamped tween; bezier: zero height, non-finite points or a handle x outside [0, 1]), or a half
 *             whose measured error exceeds APPROX_LIMIT. Examples of the latter: unclipped OutElastic split
 *             where f(m) ~ 1 (the right half's renormaliser f(1) - f(m) ~ 0.000488 collapses its amplitude),
 *             slices of Bounce close to degenerate, and Beziers whose original Phira evaluation and halves'
 *             evaluations disagree (solver divergence).
 *  - 'exact'  nominally exact family and measured error <= 1e-5 (f32 rounding of the slice argument is amplified
 *             where a curve's slope is unbounded: InOutCirc split just past 0.5 measures up to about 2.4e-4 and
 *             is reported as 'approx').
 *  - 'approx' otherwise; then maxRelError <= APPROX_LIMIT always holds.
 *
 * maxRelError is measured through Phira's own f32 evaluation path for a unit segment 0 -> 1 (relative to the
 * segment's value range |end - start|), on the 64 segment samples q = k / 64 (k = 0..63; q = 1 is excluded because
 * Phira shows the end keyframe's value there) plus 32 samples inside each half (so that a narrow half is never left
 * unmeasured): |composite(q) - original(q)|. Baked halves are not part of maxRelError (they become linear pieces).
 */

import { createBezierCurve, bezierPointAt, solveBezierT } from './bezier';
import { bezierKey } from './bezierKey';
import { clampedY, effectiveSlice, sliceIsDegenerate } from './clamped';
import { resolveRpe, rpeToTweenId, ZERO_BEZIER } from './rpeMap';
import { LINEAR_TWEEN, staticY, tweenShape } from './tweens';
import type { BezierPoints, Ease, PhiraTweenDesc, SplitResult } from './types';

const fr = Math.fround;

/** Segment samples q = k / SAMPLES. */
const SAMPLES = 64;
/** Extra samples inside each half (q = p j / HALF_SAMPLES on the left, p + (1 - p) j / HALF_SAMPLES on the right). */
const HALF_SAMPLES = 32;
/** Measured error above which a nominally exact split is reported as approximate. */
export const EXACT_TOLERANCE = 1e-5;
/** Largest measured error a kept half may have (0.1 % of the value range); above it the half is baked. */
export const APPROX_LIMIT = 1e-3;
/** |height| below which a bezier half counts as degenerate. */
const MIN_BEZIER_HEIGHT = 1e-9;
/** Renormalised bezier handles this close outside [0, 1] are rounding noise and snapped onto the range. */
const HANDLE_SNAP = 1e-12;

const HOLD: Ease = { kind: 'hold' };
const PLACEHOLDER_BEZIER: BezierPoints = [0, 0, 1, 1];

type UnitCurve = (x: number) => number;
type Side = 'left' | 'right';

function lerpF32(a: number, b: number, y: number): number {
  return fr(a + fr(fr(b - a) * y));
}

function descY(desc: PhiraTweenDesc): UnitCurve {
  switch (desc.kind) {
    case 'static':
      return (x) => staticY(desc.tween, x);
    case 'clamped':
      return (x) => clampedY(desc.tween, desc.l, desc.r, x);
    case 'bezier': {
      const curve = createBezierCurve(desc.p);
      return (x) => curve.y(x);
    }
  }
}

function tweenCurve(id: number, left: number, right: number): UnitCurve {
  return descY(resolveRpe(id, left, right, 0, ZERO_BEZIER));
}

/** The progress values q in [0, 1) at which one half of a split at p is measured. */
export function halfSampleProgress(side: Side, p: number): number[] {
  const qs: number[] = [];
  for (let k = 0; k < SAMPLES; k++) {
    const q = k / SAMPLES;
    if (side === 'left' ? q < p : q >= p) qs.push(q);
  }
  for (let j = 0; j < HALF_SAMPLES; j++) {
    qs.push(side === 'left' ? (p * j) / HALF_SAMPLES : p + ((1 - p) * j) / HALF_SAMPLES);
  }
  return qs;
}

/**
 * Max |composite - original| over the samples of one half of a unit segment 0 -> 1 split at p, the halves meeting
 * at the original value at p. Infinity when any sample is NaN.
 */
export function measureHalf(original: UnitCurve, half: UnitCurve, side: Side, p: number): number {
  const vT = lerpF32(0, 1, original(fr(p)));
  let max = 0;
  for (const q of halfSampleProgress(side, p)) {
    const o = lerpF32(0, 1, original(fr(q)));
    const s = side === 'left' ? lerpF32(0, vT, half(fr(q / p))) : lerpF32(vT, 1, half(fr((q - p) / (1 - p))));
    const dev = Math.abs(s - o);
    if (Number.isNaN(dev)) return Infinity;
    max = Math.max(max, dev);
  }
  return max;
}

/** Max of measureHalf over both halves; `left` / `right` null = that half is baked (skipped). */
export function measureSplit(original: UnitCurve, left: UnitCurve | null, right: UnitCurve | null, p: number): number {
  const l = left === null ? 0 : measureHalf(original, left, 'left', p);
  const r = right === null ? 0 : measureHalf(original, right, 'right', p);
  return Math.max(l, r);
}

/** A half's measured error, or null when the half is unusable before measuring. */
type HalfError = number | null;

function keepHalf(err: HalfError): err is number {
  // NaN and Infinity fail the comparison and are baked.
  return err !== null && err <= APPROX_LIMIT;
}

function classify(left: Ease, right: Ease, errL: HalfError, errR: HalfError, exactFamily: boolean): SplitResult {
  const keepL = keepHalf(errL);
  const keepR = keepHalf(errR);
  const maxRelError = Math.max(keepL ? errL : 0, keepR ? errR : 0);
  if (!keepL || !keepR) {
    const bakeHalves: Side[] = [];
    if (!keepL) bakeHalves.push('left');
    if (!keepR) bakeHalves.push('right');
    return { left, right, exactness: 'bake', bakeHalves, maxRelError };
  }
  const exact = exactFamily && maxRelError <= EXACT_TOLERANCE;
  return { left, right, exactness: exact ? 'exact' : 'approx', maxRelError };
}

function clamp01(v: number): number {
  return Math.min(Math.max(v, 0), 1);
}

/** The model accepts the half slice: non-empty and no division by zero in Phira's clamped tween. */
function tweenHalfValid(id: number, l: number, r: number): boolean {
  return l < r && !sliceIsDegenerate(id, l, r);
}

function splitTween(e: Ease & { readonly kind: 'tween' }, p: number): SplitResult {
  const tween = rpeToTweenId(e.id);
  if (tween <= LINEAR_TWEEN) {
    const same: Ease = { kind: 'tween', id: e.id, left: e.left, right: e.right };
    return { left: same, right: same, exactness: 'exact', maxRelError: 0 };
  }
  const slice = effectiveSlice(e);
  const lo = slice.applies ? clamp01(e.left) : 0;
  const hi = slice.applies ? clamp01(e.right) : 1;
  const m = lo + (hi - lo) * p;
  const left: Ease = { kind: 'tween', id: e.id, left: lo, right: m };
  const right: Ease = { kind: 'tween', id: e.id, left: m, right: hi };
  const original = tweenCurve(e.id, e.left, e.right);
  const errL = tweenHalfValid(e.id, lo, m) ? measureHalf(original, tweenCurve(e.id, lo, m), 'left', p) : null;
  const errR = tweenHalfValid(e.id, m, hi) ? measureHalf(original, tweenCurve(e.id, m, hi), 'right', p) : null;
  const { family } = tweenShape(tween);
  const offsetFamily = family === 'expo' || family === 'elastic';
  return classify(left, right, errL, errR, !(offsetFamily && !slice.applies));
}

function samePointsF32(a: BezierPoints, b: BezierPoints): boolean {
  return a.every((v, i) => fr(v) === fr(b[i] ?? Number.NaN));
}

/** Snaps rounding noise just outside [0, 1] onto the range; other values are returned unchanged. */
function snapHandle(v: number): number {
  if (v < 0 && v >= -HANDLE_SNAP) return 0;
  if (v > 1 && v <= 1 + HANDLE_SNAP) return 1;
  return v;
}

/** The model accepts the half: finite points and handle x in [0, 1] (validateFor's bezier rule). */
function bezierHalfValid(pts: BezierPoints): boolean {
  return pts.every((v) => Number.isFinite(v)) && pts[0] >= 0 && pts[0] <= 1 && pts[2] >= 0 && pts[2] <= 1;
}

function splitBezier(pts: BezierPoints, p: number): SplitResult {
  const [x1, y1, x2, y2] = pts;
  const t = solveBezierT(pts, p);
  const lerp = (a: number, b: number): number => a + (b - a) * t;
  // de Casteljau on P0 = (0, 0), P1, P2, P3 = (1, 1)
  const q01: readonly [number, number] = [lerp(0, x1), lerp(0, y1)];
  const q12: readonly [number, number] = [lerp(x1, x2), lerp(y1, y2)];
  const q23: readonly [number, number] = [lerp(x2, 1), lerp(y2, 1)];
  const r0: readonly [number, number] = [q01[0] + (q12[0] - q01[0]) * t, q01[1] + (q12[1] - q01[1]) * t];
  const r1: readonly [number, number] = [q12[0] + (q23[0] - q12[0]) * t, q12[1] + (q23[1] - q12[1]) * t];
  const [sx, sy] = bezierPointAt(pts, t);
  const leftShaped = Math.abs(sy) > MIN_BEZIER_HEIGHT && sx > 0;
  const rightShaped = Math.abs(1 - sy) > MIN_BEZIER_HEIGHT && sx < 1;
  const leftRaw: BezierPoints = leftShaped
    ? [snapHandle(q01[0] / sx), q01[1] / sy, snapHandle(r0[0] / sx), r0[1] / sy]
    : PLACEHOLDER_BEZIER;
  const rightRaw: BezierPoints = rightShaped
    ? [
        snapHandle((r1[0] - sx) / (1 - sx)),
        (r1[1] - sy) / (1 - sy),
        snapHandle((q23[0] - sx) / (1 - sx)),
        (q23[1] - sy) / (1 - sy),
      ]
    : PLACEHOLDER_BEZIER;
  const leftUsable = leftShaped && bezierHalfValid(leftRaw);
  const rightUsable = rightShaped && bezierHalfValid(rightRaw);
  const leftP = leftUsable ? leftRaw : PLACEHOLDER_BEZIER;
  const rightP = rightUsable ? rightRaw : PLACEHOLDER_BEZIER;
  const left: Ease = { kind: 'bezier', p: leftP };
  const right: Ease = { kind: 'bezier', p: rightP };

  const originalCurve = createBezierCurve(pts);
  const original: UnitCurve = (x) => originalCurve.y(x);
  const curveOf = (q: BezierPoints): UnitCurve => {
    const c = createBezierCurve(q);
    return (x) => c.y(x);
  };
  const errL = leftUsable ? measureHalf(original, curveOf(leftP), 'left', p) : null;
  // A kept left half registers its key first; a right half with the same key but other points is drawn by Phira
  // with the left half's points. A baked left half registers nothing.
  const collides =
    keepHalf(errL) && rightUsable && bezierKey(leftP) === bezierKey(rightP) && !samePointsF32(leftP, rightP);
  const errR = rightUsable ? measureHalf(original, curveOf(collides ? leftP : rightP), 'right', p) : null;
  return classify(left, right, errL, errR, false);
}

export function splitAt(e: Ease, p: number): SplitResult {
  if (!(p > 0 && p < 1)) throw new RangeError(`core/ease: split progress must be inside (0, 1), got ${String(p)}`);
  switch (e.kind) {
    case 'hold':
      return { left: HOLD, right: HOLD, exactness: 'exact', maxRelError: 0 };
    case 'tween':
      return splitTween(e, p);
    case 'bezier':
      return splitBezier(e.p, p);
  }
}
